import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { accountApi, clientApi } from '@/services/api'
import { useClientStore } from '@/stores/clientStore'
import type { Account, COATemplate } from '@/types'
import { Plus, Archive, ChevronDown, ChevronRight, Layers, RotateCcw, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

const TYPE_CONFIG: Record<
  string,
  {
    label: string
    borderColor: string
    headerBg: string
    headerText: string
    iconBg: string
    badgeVariant: 'info' | 'warning' | 'destructive' | 'success' | 'default'
  }
> = {
  asset: {
    label: 'Assets',
    borderColor: 'border-l-blue-500',
    headerBg: 'bg-blue-50/60',
    headerText: 'text-blue-900',
    iconBg: 'bg-blue-100 text-blue-600',
    badgeVariant: 'info',
  },
  liability: {
    label: 'Liabilities',
    borderColor: 'border-l-orange-500',
    headerBg: 'bg-orange-50/60',
    headerText: 'text-orange-900',
    iconBg: 'bg-orange-100 text-orange-600',
    badgeVariant: 'warning',
  },
  equity: {
    label: 'Equity',
    borderColor: 'border-l-purple-500',
    headerBg: 'bg-purple-50/60',
    headerText: 'text-purple-900',
    iconBg: 'bg-purple-100 text-purple-600',
    badgeVariant: 'default',
  },
  revenue: {
    label: 'Revenue',
    borderColor: 'border-l-emerald-500',
    headerBg: 'bg-emerald-50/60',
    headerText: 'text-emerald-900',
    iconBg: 'bg-emerald-100 text-emerald-600',
    badgeVariant: 'success',
  },
  expense: {
    label: 'Expenses',
    borderColor: 'border-l-red-500',
    headerBg: 'bg-red-50/60',
    headerText: 'text-red-900',
    iconBg: 'bg-red-100 text-red-600',
    badgeVariant: 'destructive',
  },
}

const TYPE_ORDER = ['asset', 'liability', 'equity', 'revenue', 'expense'] as const

const TEMPLATE_ACCENTS: Record<string, { bg: string; ring: string }> = {
  general: { bg: 'bg-[#0078C8]/10', ring: 'ring-[#0078C8]/20' },
  retail: { bg: 'bg-emerald-50', ring: 'ring-emerald-200' },
  services: { bg: 'bg-purple-50', ring: 'ring-purple-200' },
  property: { bg: 'bg-amber-50', ring: 'ring-amber-200' },
}

export default function ChartOfAccounts() {
  const { clientId } = useParams<{ clientId: string }>()
  const setCurrentClient = useClientStore((s) => s.setCurrentClient)

  const [accounts, setAccounts] = useState<Account[]>([])
  const [templates, setTemplates] = useState<COATemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [showArchived, setShowArchived] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [showTemplates, setShowTemplates] = useState(false)
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(
    new Set(['asset', 'liability', 'equity', 'revenue', 'expense']),
  )
  const [newAccount, setNewAccount] = useState({
    code: '',
    name: '',
    account_type: 'asset',
    sub_type: '',
    description: '',
  })
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!clientId) return
    loadAccounts()
    loadTemplates()
    clientApi.get(clientId).then((res) => setCurrentClient(res.data.client))
  }, [clientId])

  async function loadAccounts() {
    setLoading(true)
    try {
      const res = await accountApi.list(clientId!, showArchived)
      setAccounts(res.data.accounts)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!clientId) return
    loadAccounts()
  }, [showArchived])

  async function loadTemplates() {
    try {
      const res = await accountApi.listTemplates()
      setTemplates(res.data.templates)
    } catch {
      /* templates may not be available yet */
    }
  }

  async function handleApplyTemplate(key: string) {
    try {
      await accountApi.applyTemplate({ client_ledger_id: clientId!, template_key: key })
      toast.success('Template applied successfully')
      loadAccounts()
      setShowTemplates(false)
    } catch {
      toast.error('Failed to apply template')
    }
  }

  async function handleCreateAccount(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    try {
      await accountApi.create({ client_ledger_id: clientId!, ...newAccount })
      toast.success('Account created')
      setNewAccount({ code: '', name: '', account_type: 'asset', sub_type: '', description: '' })
      setShowAdd(false)
      loadAccounts()
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to create account'
      toast.error(message)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleToggleArchive(account: Account) {
    try {
      await accountApi.archive(account.id)
      toast.success(account.is_archived ? 'Account restored' : 'Account archived')
      loadAccounts()
    } catch {
      toast.error('Action failed')
    }
  }

  const grouped = accounts.reduce<Record<string, Account[]>>((acc, a) => {
    if (!acc[a.account_type]) acc[a.account_type] = []
    acc[a.account_type].push(a)
    return acc
  }, {})

  function toggleType(type: string) {
    const next = new Set(expandedTypes)
    if (next.has(type)) next.delete(type)
    else next.add(type)
    setExpandedTypes(next)
  }

  const totalAccounts = accounts.length
  const activeCount = accounts.filter((a) => !a.is_archived).length
  const archivedCount = accounts.filter((a) => a.is_archived).length

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-7 w-48" />
          <div className="flex gap-2">
            <Skeleton className="h-9 w-36" />
            <Skeleton className="h-9 w-32" />
          </div>
        </div>
        <Card>
          <CardContent className="p-5 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Chart of Accounts</h1>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">
            {totalAccounts} accounts &middot; {activeCount} active
            {archivedCount > 0 && <span> &middot; {archivedCount} archived</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowTemplates(!showTemplates)}>
            <Layers className="w-4 h-4" />
            Apply Template
          </Button>
          <Button variant="create" onClick={() => setShowAdd(!showAdd)}>
            <Plus className="w-4 h-4" />
            Add Account
          </Button>
        </div>
      </div>

      {/* Template Picker */}
      {showTemplates && (
        <Card className="overflow-hidden">
          <CardHeader className="border-b px-5 py-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Select a Template</CardTitle>
                <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">
                  Apply a pre-built chart of accounts to get started quickly
                </p>
              </div>
              <Button variant="ghost" size="icon-sm" onClick={() => setShowTemplates(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {templates.map((t) => {
                const accent = TEMPLATE_ACCENTS[t.key] || TEMPLATE_ACCENTS.general
                return (
                  <button
                    key={t.key}
                    onClick={() => handleApplyTemplate(t.key)}
                    className={`group text-left rounded-lg border border-border p-4
                      hover:shadow-md hover:border-[#0078C8]/30 transition-all duration-150
                      ring-1 ring-transparent hover:${accent.ring}`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${accent.bg}`}>
                      <Layers className="w-4 h-4 text-[#000A1E]" />
                    </div>
                    <p className="text-[15px] font-bold text-[#000A1E] group-hover:text-[#0078C8] transition-colors">
                      {t.name}
                    </p>
                    <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-1 leading-relaxed">{t.description}</p>
                  </button>
                )
              })}
              {templates.length === 0 && (
                <div className="col-span-full text-center py-6 text-[15px] text-[rgba(0,10,30,0.75)]">
                  No templates available
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Account Inline Form */}
      {showAdd && (
        <Card className="overflow-hidden">
          <CardHeader className="border-b px-5 py-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[17px] font-bold text-[#000A1E] leading-[24px]">New Account</CardTitle>
              <Button variant="ghost" size="icon-sm" onClick={() => setShowAdd(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-5">
            <form onSubmit={handleCreateAccount}>
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-2 space-y-1.5">
                  <Label>Code</Label>
                  <Input
                    value={newAccount.code}
                    onChange={(e) => setNewAccount({ ...newAccount, code: e.target.value })}
                    className="font-mono"
                    placeholder="1000"
                    required
                  />
                </div>
                <div className="col-span-3 space-y-1.5">
                  <Label>Account Name</Label>
                  <Input
                    value={newAccount.name}
                    onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
                    placeholder="e.g. Cash at Bank"
                    required
                  />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label>Type</Label>
                  <select
                    value={newAccount.account_type}
                    onChange={(e) => setNewAccount({ ...newAccount, account_type: e.target.value })}
                    className="input"
                  >
                    {TYPE_ORDER.map((t) => (
                      <option key={t} value={t}>{TYPE_CONFIG[t].label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label>Sub Type</Label>
                  <Input
                    value={newAccount.sub_type}
                    onChange={(e) => setNewAccount({ ...newAccount, sub_type: e.target.value })}
                    placeholder="e.g. Bank"
                  />
                </div>
                <div className="col-span-3 space-y-1.5">
                  <Label>Description</Label>
                  <Input
                    value={newAccount.description}
                    onChange={(e) => setNewAccount({ ...newAccount, description: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-border">
                <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="create" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create Account'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Account Tree */}
      <Card className="overflow-hidden">
        {/* Toolbar */}
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="text-[13px] h-7"
              onClick={() => setExpandedTypes(new Set(TYPE_ORDER))}
            >
              Expand All
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-[13px] h-7"
              onClick={() => setExpandedTypes(new Set())}
            >
              Collapse All
            </Button>
          </div>
          <label className="flex items-center gap-2 text-[13px] text-[rgba(0,10,30,0.75)] cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="rounded border-border text-[#0078C8] focus:ring-[#0078C8]/20"
            />
            Show archived
          </label>
        </div>

        {/* Type sections */}
        {TYPE_ORDER.map((type) => {
          const config = TYPE_CONFIG[type]
          const typeAccounts = grouped[type] || []
          const expanded = expandedTypes.has(type)
          const active = typeAccounts.filter((a) => !a.is_archived)
          const archived = typeAccounts.filter((a) => a.is_archived)

          return (
            <div key={type} className="border-b border-border last:border-b-0">
              {/* Section header */}
              <button
                onClick={() => toggleType(type)}
                className={cn(
                  'flex items-center gap-3 w-full px-5 py-3.5 text-left transition-colors hover:bg-muted/30 border-l-4',
                  config.borderColor,
                  expanded && config.headerBg
                )}
              >
                <div className={`w-6 h-6 rounded flex items-center justify-center ${config.iconBg}`}>
                  {expanded ? (
                    <ChevronDown className="w-3.5 h-3.5" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5" />
                  )}
                </div>
                <span className={`text-[15px] font-bold ${config.headerText}`}>
                  {config.label}
                </span>
                <Badge variant={config.badgeVariant}>
                  {typeAccounts.length}
                </Badge>
              </button>

              {/* Account rows */}
              {expanded && typeAccounts.length > 0 && (
                <div className={`border-l-4 ${config.borderColor}`}>
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead className="pl-14 w-28 text-[13px]">Code</TableHead>
                        <TableHead className="text-[13px]">Account Name</TableHead>
                        <TableHead className="w-40 text-[13px]">Sub Type</TableHead>
                        <TableHead className="w-24 text-center text-[13px]">Status</TableHead>
                        <TableHead className="w-20 text-right pr-5 text-[13px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {active.map((acc) => (
                        <TableRow key={acc.id}>
                          <TableCell className="pl-14">
                            <span className="font-mono text-[13px] text-[rgba(0,10,30,0.75)] bg-muted px-1.5 py-0.5 rounded">
                              {acc.code}
                            </span>
                          </TableCell>
                          <TableCell className="text-[15px] font-bold text-[#000A1E]">
                            {acc.name}
                            {acc.description && (
                              <span className="block text-[13px] text-[rgba(0,10,30,0.75)] font-normal mt-0.5">
                                {acc.description}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {acc.sub_type && (
                              <span className="text-[13px] text-[rgba(0,10,30,0.75)] capitalize">
                                {acc.sub_type.replace(/_/g, ' ')}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {acc.is_system ? (
                              <Badge variant="info">System</Badge>
                            ) : (
                              <Badge variant="success">Active</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right pr-5">
                            {!acc.is_system && (
                              <Button
                                variant="ghost" size="icon-sm"
                                onClick={() => handleToggleArchive(acc)}
                                title="Archive account"
                              >
                                <Archive className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {archived.map((acc) => (
                        <TableRow key={acc.id} className="opacity-60">
                          <TableCell className="pl-14">
                            <span className="font-mono text-[13px] text-[rgba(0,10,30,0.75)]/70 bg-muted px-1.5 py-0.5 rounded">
                              {acc.code}
                            </span>
                          </TableCell>
                          <TableCell className="text-[rgba(0,10,30,0.75)] line-through">
                            {acc.name}
                          </TableCell>
                          <TableCell>
                            {acc.sub_type && (
                              <span className="text-[13px] text-[rgba(0,10,30,0.75)] capitalize">
                                {acc.sub_type.replace(/_/g, ' ')}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="default">Archived</Badge>
                          </TableCell>
                          <TableCell className="text-right pr-5">
                            <Button
                              variant="ghost" size="icon-sm"
                              onClick={() => handleToggleArchive(acc)}
                              title="Restore account"
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Empty type */}
              {expanded && typeAccounts.length === 0 && (
                <div className={`border-l-4 ${config.borderColor} px-14 py-6 text-[15px] text-[rgba(0,10,30,0.75)]`}>
                  No {config.label.toLowerCase()} accounts
                </div>
              )}
            </div>
          )
        })}

        {/* Global empty state */}
        {accounts.length === 0 && (
          <div className="px-5 py-16 text-center">
            <div className="w-14 h-14 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Layers className="w-7 h-7 text-[rgba(0,10,30,0.75)]" />
            </div>
            <h3 className="text-[17px] font-bold text-[#000A1E] leading-[24px] mb-1">No accounts yet</h3>
            <p className="text-[15px] text-[rgba(0,10,30,0.75)] mb-5 max-w-xs mx-auto">
              Get started by applying a template or adding accounts manually.
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button variant="outline" onClick={() => setShowTemplates(true)}>
                <Layers className="w-4 h-4" />
                Apply Template
              </Button>
              <Button variant="create" onClick={() => setShowAdd(true)}>
                <Plus className="w-4 h-4" />
                Add Account
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
